import { useAccountUsers } from '@my/directory-api/hooks';
import type { AccountUserStatusValue, AppNameValue, User } from '@my/directory-api';
import { ErrorBanner, Loading } from '@my/ui';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { appPillClass } from './lib/appColours';
import { useAccountContext } from './useAccountContext';

const STATUSES: AccountUserStatusValue[] = ['Active', 'Invited', 'Suspended', 'Left'];
const APPS: AppNameValue[] = ['Tms', 'Depot', 'Subsistence', 'Safety', 'Check'];

type StatusFilter = 'all' | AccountUserStatusValue;
type AppFilter = 'all' | 'none' | AppNameValue;

/**
 * Who is in this account, and what each of them can reach.
 *
 * <p>
 * ACCESS IS THE POINT OF THIS SCREEN. A name and an email answer nothing anyone
 * comes here to ask; "which organisations can they see, and which apps in each"
 * is the question, and it is otherwise only answerable by opening every app in
 * turn.
 * </p>
 *
 * <p>
 * Read-only. Granting and revoking app roles lives in each app's Settings →
 * Users &amp; Access, beside the capability model that gives those roles
 * meaning — reproducing it here would mean keeping two editors honest.
 * </p>
 */
export function AccountUsersPage() {
  const { account, isReady } = useAccountContext();
  const users = useAccountUsers({ enabled: isReady });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [filterApp, setFilterApp] = useState<AppFilter>('all');

  const organisationNames = useMemo(
    () => new Map(account.organisations.map((o) => [o.id, o.name])),
    [account.organisations],
  );

  const hasActiveFilters = status !== 'all' || filterApp !== 'all' || search.trim().length > 0;
  const clearFilters = () => {
    setStatus('all');
    setFilterApp('all');
    setSearch('');
  };

  const visible = useMemo(() => {
    let list = users.data ?? [];

    const membershipOf = (u: User) => u.accounts?.find((a) => a.id === account.id);

    if (status !== 'all') list = list.filter((u) => membershipOf(u)?.status === status);

    if (filterApp === 'none') {
      list = list.filter((u) => (membershipOf(u)?.appRoles ?? []).length === 0);
    } else if (filterApp !== 'all') {
      list = list.filter((u) =>
        (membershipOf(u)?.appRoles ?? []).some((r) => r.app === filterApp),
      );
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          (u.fullName ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q),
      );
    }

    return list;
  }, [users.data, account.id, status, filterApp, search]);

  if (!isReady || users.isLoading) return <Loading label="Users" />;
  if (users.error) {
    return <ErrorBanner title="Failed to load users" message={users.error.message} />;
  }

  return (
    <>
      {/* Filters left, search right — the parties-list chrome, so somebody
          moving between the apps does not have to look for the search box. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {(['all', ...STATUSES] as StatusFilter[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={chipClass(status === s)}
          >
            {s === 'all' ? 'All' : s}
          </button>
        ))}

        <div className="mx-1 h-4 w-px bg-slate-300" aria-hidden="true" />

        {(['all', ...APPS, 'none'] as AppFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilterApp(f)}
            className={chipClass(filterApp === f)}
          >
            {f === 'all' ? 'All apps' : f === 'none' ? 'No access' : f}
          </button>
        ))}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="ml-1 text-[10px] text-slate-400 underline hover:text-slate-600"
          >
            Clear
          </button>
        )}

        <div className="flex-1" />

        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users…"
            aria-label="Search users"
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md">
        <div className="min-w-[768px]">
          <table className="w-full table-auto text-xs">
            <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
              <tr>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">User</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Account role</th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-xs italic text-slate-400">
                    {hasActiveFilters
                      ? 'No users match these filters.'
                      : 'No users in this account.'}
                  </td>
                </tr>
              ) : (
                visible.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    accountId={account.id}
                    organisationNames={organisationNames}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function UserRow({
  user,
  accountId,
  organisationNames,
}: {
  user: User;
  accountId: string;
  organisationNames: Map<string, string>;
}) {
  // A user can belong to several accounts — reading the first would report
  // their role at a different company.
  const membership = user.accounts?.find((a) => a.id === accountId);

  // Grouped by organisation, because "where can they work" is the first half of
  // the question and "what can they do there" is the second. A flat row of app
  // pills answers neither on its own.
  const byOrganisation = new Map<string, { app: string; role: string }[]>();
  for (const role of membership?.appRoles ?? []) {
    const list = byOrganisation.get(role.organisationId) ?? [];
    list.push({ app: role.app, role: role.role });
    byOrganisation.set(role.organisationId, list);
  }

  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-3 py-1 align-top">
        <div className="text-xs font-semibold text-slate-900">
          {user.fullName || <span className="italic text-slate-300">—</span>}
        </div>
        <div className="text-[11px] text-slate-500">{user.email}</div>
      </td>

      <td className="whitespace-nowrap px-3 py-1 align-top text-xs">
        {membership ? (
          <span className={roleChipClass(membership.role)}>{membership.role}</span>
        ) : (
          <span className="italic text-slate-300">—</span>
        )}
      </td>

      <td className="whitespace-nowrap px-3 py-1 align-top text-xs">
        {membership ? (
          <span className={statusChipClass(membership.status)}>{membership.status}</span>
        ) : (
          <span className="italic text-slate-300">—</span>
        )}
      </td>

      <td className="px-3 py-1 align-top text-xs">
        {byOrganisation.size === 0 ? (
          // Worth saying plainly: a member with no app role can sign in and
          // reach nothing, which reads as a broken account until you know.
          <span className="italic text-slate-400">No app access</span>
        ) : (
          <div className="space-y-1">
            {[...byOrganisation.entries()].map(([organisationId, roles]) => (
              <div key={organisationId} className="flex flex-wrap items-center gap-1">
                <span className="mr-1 text-[11px] font-medium text-slate-700">
                  {organisationNames.get(organisationId) ?? 'Unknown organisation'}
                </span>
                {roles.map(({ app, role }) => (
                  <span key={`${app}-${role}`} className={appPillClass(app)}>
                    {app} · {role}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

/**
 * Account role. Owner stands out and Member stays quiet, which is the right way
 * round: nearly everyone is a Member, so colouring them would mean colouring
 * the whole column and telling you nothing. The eye should land on the one or
 * two people who can change the account.
 */
function roleChipClass(role: string): string {
  const base = 'inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium';
  switch (role) {
    case 'Owner':
      return `${base} border-purple-200 bg-purple-50 text-purple-700`;
    case 'Admin':
      return `${base} border-sky-200 bg-sky-50 text-sky-700`;
    case 'Billing':
      return `${base} border-orange-200 bg-orange-50 text-orange-700`;
    default:
      return `${base} border-slate-200 bg-slate-50 text-slate-600`;
  }
}

/**
 * Membership status.
 *
 * <p>
 * Four states, not two: Invited was sharing amber with Suspended, which reads
 * as the same kind of thing when one is somebody on their way in and the other
 * is somebody shut out.
 * </p>
 */
function statusChipClass(status: string): string {
  const base = 'inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium';
  switch (status) {
    case 'Active':
      return `${base} border-green-200 bg-green-50 text-green-700`;
    case 'Invited':
      return `${base} border-amber-200 bg-amber-50 text-amber-700`;
    case 'Suspended':
      return `${base} border-rose-200 bg-rose-50 text-rose-700`;
    default:
      return `${base} border-slate-200 bg-slate-50 text-slate-500`;
  }
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
